import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Heading,
  RadioButton,
  RadioButtonGroup,
  Text,
  Modal,
  ModalContent,
  ModalTitle,
  ModalFooter,
} from "suomifi-ui-components";
import { IconArrowLeft, IconRemove } from "suomifi-icons";
import { useAppState } from "../state/store";
import {
  voiNahda,
  ADMIN_GROUP_ID,
  HANDLER_GROUP_ID,
  ASPA_GROUP_ID,
  REPORTS_GROUP_ID,
  RIGHTS_GROUP_ID,
  laskeEfektiivinenPaasy,
  PERUSTYO_VAIHTOEHDOT,
  type Perustyo,
  annaProfiiliTrivoreKayttajalle,
  profiiliToGroupIds,
} from "../utils/oikeudet";
import { useModaaliBackdropSulku } from "../utils/modaaliBackdropSulku";
import TuhoavaButton from "../components/TuhoavaButton";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import {
  useGetOfficersQuery,
  useUpdateUserGroupsMutation,
  useDeleteOfficerMutation,
  useRecoverOfficerMutation,
} from "../api/backendApi";
import { useTranslation } from "react-i18next";

/**
 * Käyttöoikeuksien myöntökortti — yksittäisen virkailijan roolien muokkaus.
 * Lähde: 10.6.2026_UX-suunnittelu_kayttooikeuksien_myontaminen.md §3.3.
 *
 * Perustyö (RadioButton, sisäkkäinen tikapuu) + Lisäoikeudet (Checkbox,
 * ortogonaalisia). Efektiivisen pääsyn yhteenveto reaaliajassa — sama helper
 * kuin DemoBanner (laskeEfektiivinenPaasy).
 *
 * Turvallisuus (§4):
 * - Oma rivi vain luettava (estetään omien oikeuksien nosto)
 * - K5-päätös (10.6. jälkeen): käyttöoikeuksien myöntäjä saa myöntää myös
 *   admin- ja myöntäjä-roolin (kumoaa UX-dok §4.1 escalation-rajauksen,
 *   joka oli "katselmoitava DVV:n kanssa" → tuoteomistajan päätös).
 */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const BackButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.highlightBase};
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
  & > svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Layout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.l};
  align-items: flex-start;
`;

const Vasen = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const Oikea = styled.div`
  width: 22rem;
  flex-shrink: 0;
`;

const Kortit = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const SektioOtsikko = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
  border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase};
  padding-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const AdminHuomio = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const Toiminnot = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.s};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.s};
`;

/* Efektiivisen pääsyn yhteenveto */
const PaasyPanel = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  position: sticky;
  top: 4rem;
`;

const PaasyOtsikko = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
  border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase};
  padding-bottom: ${({ theme }) => theme.spacing.xxs};
  margin-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const PaasyRivi = styled.div<{ $ok: boolean }>`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme, $ok }) =>
    $ok ? theme.colors.blackBase : theme.colors.depthDark1};
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxs};

  &::before {
    content: "${({ $ok }) => ($ok ? "✓" : "✗")}";
    flex-shrink: 0;
    color: ${({ theme, $ok }) =>
      $ok ? theme.colors.successDark1 : theme.colors.depthDark1};
  }
`;

export default function KayttooikeusKortti() {
  const { t, i18n } = useTranslation(["oikeudet", "common"]);
  const { uid } = useParams();
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();
  const { kayttaja, todellinenRooli, isLoading } = useTodellinenRooli();

  const { data: officers, isLoading: officersLataa } = useGetOfficersQuery();
  const officer = officers?.find((o) => o.id === uid);

  const nykyProfiili = annaProfiiliTrivoreKayttajalle({
    memberOf: officer?.memberOf,
  });

  const omaRivi = officer?.id === kayttaja?.id;
  // K5-päätös: myöntäjä (ja admin) saa myöntää kaikki roolit ml. admin/myöntö.
  // Näkymään pääsee vain myonto/admin (route-suoja), joten ei lisärajausta.

  // Johdetaan nykyinen profiili rooli-stringistä (yksinkertaistus proto-mockeille)
  function johdettuPerustyo(): Perustyo {
    const r = officer!.memberOf;
    if (r.includes(ADMIN_GROUP_ID)) return "admin";
    if (r.includes(HANDLER_GROUP_ID)) return "kasittelija";
    if (r.includes(ASPA_GROUP_ID)) return "aspa";
    return "ei";
  }

  const [perustyo, setPerustyo] = useState<Perustyo>(johdettuPerustyo());
  const [lisaRaportit, setLisaRaportit] = useState(
    officer?.memberOf.includes(REPORTS_GROUP_ID),
  );

  const [lisaMyonto, setLisaMyonto] = useState(
    officer?.memberOf.includes(RIGHTS_GROUP_ID),
  );
  const [confirmAuki, setConfirmAuki] = useState(false);
  const [poistoAuki, setPoistoAuki] = useState(false);
  useModaaliBackdropSulku(confirmAuki, () => setConfirmAuki(false));
  useModaaliBackdropSulku(poistoAuki, () => setPoistoAuki(false));
  const [recoverOfficer] = useRecoverOfficerMutation();

  const valittuProfiili = {
    perustyo,
    lisaoikeudet: {
      raportit: lisaRaportit || perustyo === "admin",
      myonto: lisaMyonto || perustyo === "admin",
    },
  };
  const efektiivinen = laskeEfektiivinenPaasy(valittuProfiili);

  const adminValittu = perustyo === "admin";
  async function palautaVirkailija() {
    await recoverOfficer(officer!.id).unwrap();

    const groupIds = profiiliToGroupIds(valittuProfiili);
    await updateUserGroups({
      id: officer!.id,
      memberOf: groupIds,
    }).unwrap();

    setPalautusConfirmAuki(false);
    navigate("/kayttooikeudet");
  }
  const [updateUserGroups] = useUpdateUserGroupsMutation();
  const [deleteOfficer] = useDeleteOfficerMutation();
  const [palautusConfirmAuki, setPalautusConfirmAuki] = useState(false);
  useModaaliBackdropSulku(palautusConfirmAuki, () =>
    setPalautusConfirmAuki(false),
  );

  if (!voiNahda("navi.kayttooikeudet", todellinenRooli?.profiili)) {
    return <Navigate to="/" replace />;
  }

  if (!officer) {
    return (
      <div>
        {t("ei.virkailijaa")}
        {uid}
      </div>
    );
  }

  async function tallenna() {
    const groupIds = profiiliToGroupIds(valittuProfiili);
    await updateUserGroups({ id: officer!.id, memberOf: groupIds }).unwrap();
    setConfirmAuki(false);
    navigate("/kayttooikeudet");
  }

  async function poistaVirkailija() {
    await deleteOfficer(officer!.id).unwrap();

    setPoistoAuki(false);
    navigate("/kayttooikeudet");
  }

  const piilotaRaportit = import.meta.env.VITE_PIILOTA_RAPORTIT === "true";
  return (
    <Page>
      <BackButton type="button" onClick={() => navigate("/kayttooikeudet")}>
        <IconArrowLeft aria-hidden />
        {t("takaisin.virkailijaluetteloon")}
      </BackButton>

      <Heading variant="h1">
        {officer.name.givenName} {officer.name.familyName}
      </Heading>
      <Text>
        {officer.email} ·{" "}
        {!officer.locked && !officer.softDeleted
          ? t("tilat.aktiivinen")
          : t("tilat.passivoitu")}
      </Text>

      {omaRivi && (
        <Text style={{ fontStyle: "italic", color: "#555" }}>
          {t("huomiot.oma_rivi")}
        </Text>
      )}

      <Layout>
        <Vasen>
          <Kortit>
            <SektioOtsikko>{t("otsikot.perustyo")}</SektioOtsikko>
            <RadioButtonGroup
              labelText={t("otsikot.perustyo")}
              name="perustyo"
              value={perustyo}
              onChange={(v: string) => !omaRivi && setPerustyo(v as Perustyo)}
            >
              {PERUSTYO_VAIHTOEHDOT.map((opt) => (
                <RadioButton
                  key={opt.arvo}
                  value={opt.arvo}
                  disabled={omaRivi}
                  hintText={t(`perustyo.${opt.arvo}.kuvaus`)}
                >
                  {t(`perustyo.${opt.arvo}.nimi`)}
                </RadioButton>
              ))}
            </RadioButtonGroup>
          </Kortit>

          <Kortit>
            <SektioOtsikko>{t("otsikot.lisaoikeudet")}</SektioOtsikko>
            {/* D1 (22.6.2026): lisäoikeudet näytetään aina; admin-tasolla
                rastittuina ja lukittuina, jotta näkee mitä admin sisältää. */}
            <CheckboxGroup labelText="Lisäoikeudet" labelMode="hidden">
              {!piilotaRaportit && (
                <Checkbox
                  checked={lisaRaportit || adminValittu}
                  disabled={omaRivi || adminValittu}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    !omaRivi && setLisaRaportit(e.target.checked)
                  }
                  hintText={t("vinkit.raportit")}
                >
                  {t("labels.raportit")}
                </Checkbox>
              )}
              <Checkbox
                checked={lisaMyonto || adminValittu}
                disabled={omaRivi || adminValittu}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  !omaRivi && setLisaMyonto(e.target.checked)
                }
                hintText={t("vinkit.oikeudet")}
              >
                {t("labels.oikeudet")}
              </Checkbox>
            </CheckboxGroup>
            {adminValittu && (
              <AdminHuomio>{t("huomiot.admin_rooli")}</AdminHuomio>
            )}
          </Kortit>

          {!omaRivi && (
            <Toiminnot>
              {officer.softDeleted ? (
                <Button onClick={() => setPalautusConfirmAuki(true)}>
                  {t("napit.palauta")}
                </Button>
              ) : (
                <>
                  <Button onClick={() => setConfirmAuki(true)}>
                    {t("napit.tallenna")}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/kayttooikeudet")}
                  >
                    {t("napit.peruuta")}
                  </Button>
                  <TuhoavaButton
                    icon={<IconRemove />}
                    onClick={() => setPoistoAuki(true)}
                  >
                    {t("napit.poista")}
                  </TuhoavaButton>
                </>
              )}
            </Toiminnot>
          )}
        </Vasen>

        <Oikea>
          <PaasyPanel>
            <PaasyOtsikko>{t("otsikot.paasy")}</PaasyOtsikko>
            {efektiivinen.kaytossa.map((toiminto) => (
              <PaasyRivi key={toiminto} $ok>
                {t(`common:toiminnot.${toiminto}`)}
              </PaasyRivi>
            ))}
            {efektiivinen.kaytossa.length === 0 && (
              <PaasyRivi $ok={false}>{t("tekstit.paasy")}</PaasyRivi>
            )}
          </PaasyPanel>
        </Oikea>
      </Layout>

      <Modal
        appElementId="root"
        visible={confirmAuki}
        onEscKeyDown={() => setConfirmAuki(false)}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>{t("otsikot.myontaminen")}</ModalTitle>
          <Text>
            {t("tekstit.myontaminen.myonna")}{" "}
            <strong>
              {officer.name.givenName} {officer.name.familyName}
            </strong>
            : {t(`perustyo.${perustyo}.nimi`)}
            {lisaRaportit && !adminValittu
              ? t("tekstit.myontaminen.raportit")
              : ""}
            {lisaMyonto && !adminValittu
              ? t("tekstit.myontaminen.oikeudet")
              : ""}
            ?
          </Text>
          <Text
            style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#555" }}
          >
            {t("tekstit.audit")}
          </Text>
        </ModalContent>
        <ModalFooter>
          <Button onClick={tallenna}>Vahvista</Button>
          <Button variant="secondary" onClick={() => setConfirmAuki(false)}>
            Peruuta
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        appElementId="root"
        visible={poistoAuki}
        onEscKeyDown={() => setPoistoAuki(false)}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>{t("otsikot.poista_kayttaja")}</ModalTitle>
          <Text>
            {t("tekstit.poista_kayttaja1")}{" "}
            <strong>
              {officer.name.givenName} {officer.name.familyName}
            </strong>{" "}
            {t("tekstit.poista_kayttaja2")}
          </Text>
          <div>
            <Text
              style={{
                marginTop: "0.5rem",
                fontSize: "0.875rem",
                color: "#555",
              }}
            >
              {t("huomiot.kirjaus")}
            </Text>
          </div>
        </ModalContent>
        <ModalFooter>
          <TuhoavaButton onClick={poistaVirkailija}>
            {t("napit.poista2")}
          </TuhoavaButton>
          <Button variant="secondary" onClick={() => setPoistoAuki(false)}>
            {t("napit.peruuta")}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        appElementId="root"
        visible={palautusConfirmAuki}
        onEscKeyDown={() => setPalautusConfirmAuki(false)}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>{t("otsikot.palauta")}?</ModalTitle>
          <Text>
            {t("tekstit.palauta_kayttaja1")}{" "}
            <strong>
              {officer.name.givenName} {officer.name.familyName}
            </strong>{" "}
            {t("tekstit.palauta_kayttaja2")}?
          </Text>
          <div>
            <Text
              style={{
                marginTop: "0.5rem",
                fontSize: "0.875rem",
                color: "#555",
              }}
            >
              {t("huomiot.palautus")}
            </Text>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button onClick={palautaVirkailija}>{t("napit.palauta2")}</Button>
          <Button
            variant="secondary"
            onClick={() => setPalautusConfirmAuki(false)}
          >
            {t("napit.peruuta")}
          </Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
}
